import { CSSReset, ChakraProvider, extendTheme } from '@chakra-ui/react';
import {
  CHECKOUT_THEMES,
  type CheckoutTheme,
  DEFAULT_CHECKOUT_THEME,
  LeafThemeProvider,
  getThemeByName,
  theme as leafTheme,
} from '@getjust/leaf';
import { type ReactNode, createContext, useContext, useEffect, useMemo } from 'react';

import { useBrandingPreviewListener } from '$hooks/client/useBrandingPreviewListener';
import { useShop } from '$hooks/queries/useShop';
import { useROFormMessageAtom } from '$hooks/state';
import { useROBrandingPreviewAtom } from '$hooks/state/useBrandingPreviewAtom';

interface ThemeContextValue {
  themeName: CheckoutTheme;
  brandColorPrimary?: string;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextValue>({
  themeName: DEFAULT_CHECKOUT_THEME,
  isLoading: false,
});

export const useThemeContext = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Wrapper component that provides the appropriate Chakra theme based on shop settings.
 * This component should wrap the entire app content that needs theming.
 */
export function DynamicThemeProvider({ children }: ThemeProviderProps) {
  const { data: shop, isLoading } = useShop();
  const message = useROFormMessageAtom();
  useBrandingPreviewListener({ enabled: message?.sessionKey === 'PREVIEW_SESSION' });
  const brandingPreview = useROBrandingPreviewAtom();

  const themeName: CheckoutTheme = useMemo(() => {
    if (brandingPreview) {
      return brandingPreview.checkoutTheme;
    }
    const shopTheme = shop?.settings?.checkoutTheme;
    if (shopTheme && CHECKOUT_THEMES.includes(shopTheme)) {
      return shopTheme as CheckoutTheme;
    }
    return DEFAULT_CHECKOUT_THEME;
  }, [shop?.settings?.checkoutTheme, brandingPreview]);

  // Get the base theme for the selected theme name
  const baseTheme = useMemo(() => getThemeByName(themeName), [themeName]);

  // Extend with global styles
  const theme = useMemo(
    () =>
      extendTheme({
        ...baseTheme,
        styles: {
          global: () => ({
            body: {
              bg: 'var(--bg, #ffffff)',
              color: 'var(--text, #1C1C1C)',
            },
          }),
        },
      }),
    [baseTheme, themeName],
  );

  // Apply theme attribute to body
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.setAttribute('data-theme', themeName);
    }
  }, [themeName]);

  const brandColorPrimary = brandingPreview?.brandStyles?.brandColorPrimary ?? shop?.settings?.brandStyles?.brandColorPrimary;

  const contextValue = useMemo(
    () => ({
      themeName,
      brandColorPrimary,
      isLoading,
    }),
    [themeName, brandColorPrimary, isLoading],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <ChakraProvider resetCSS theme={theme}>
        <CSSReset />
        <LeafThemeProvider themeName={themeName}>{children}</LeafThemeProvider>
      </ChakraProvider>
    </ThemeContext.Provider>
  );
}

/**
 * Static theme provider that uses Aurora theme.
 * Used before shop data is loaded.
 */
export function StaticThemeProvider({ children }: ThemeProviderProps) {
  const theme = useMemo(
    () =>
      extendTheme({
        ...leafTheme,
        styles: {
          global: () => ({
            body: {
              bg: 'var(--bg, #ffffff)',
              color: 'var(--text, #1C1C1C)',
            },
          }),
        },
      }),
    [],
  );

  return (
    <ChakraProvider resetCSS theme={theme}>
      <CSSReset />
      <LeafThemeProvider themeName={DEFAULT_CHECKOUT_THEME}>{children}</LeafThemeProvider>
    </ChakraProvider>
  );
}
